import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { compliantKey } from '@/helpers/compliant-register';
import { flash, modal } from '@/helpers/ui';
import {
  type OrderItem,
  useRegisterOrderStore,
} from '@/modules/register/stores/order';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useRegisterDrawerStore } from '@/stores/register-drawer';
import { useUserStore } from '@/stores/user';
import { mutateOrder } from '../actions/order';

const { hasFeatureFlag } = useUserStore();

export const useOrderSubmit = () => {
  const { order, reset } = useRegisterOrderStore();
  const { cashupEnabled, showOpenDrawerModal, savingDraft } = storeToRefs(
    useRegisterStore(),
  );
  const { isOpen: isDrawerOpen, isLoadingDrawerStatus } = storeToRefs(
    useRegisterDrawerStore(),
  );
  const router = useRouter();
  const { t } = useI18n();

  const goToPayment = () => router.push({ name: 'register-payment' });

  const checkoutOrder = () => {
    if (isLoadingDrawerStatus.value) return;
    if (cashupEnabled.value && !isDrawerOpen.value) {
      showOpenDrawerModal.value = true;
    } else {
      goToPayment();
    }
  };

  const { call: callOrderMutation, onDone: onDoneMutation } = mutateOrder();

  onDoneMutation(({ data }) => {
    if (data) {
      handleDraftResponse(data.createOrder || data.updateOrder);
    }
  });

  const handleDraftResponse = ({ errors }: any) => {
    if (errors) {
      modal('warning', {
        message: t('register.draft_error'),
      });
    } else {
      flash(t(compliantKey('register.saved_as_draft')));
    }
    savingDraft.value = false;
    reset();
  };

  const saveDraft = () => {
    const noCards = !order.items.find(
      (item: OrderItem) => item.giftcard || item.prepaidCard,
    );

    if (hasFeatureFlag('compliant-register') && order.transactions.length > 0) {
      modal('warning', {
        message: t('register.draft_with_transactions_notice'),
      });
      savingDraft.value = false;
      return;
    }

    if (noCards) {
      if (order.id) {
        callOrderMutation('update');
      } else {
        order.draft = true;
        callOrderMutation('create');
      }
    } else {
      modal('warning', { message: t('register.draft_notice') });
      savingDraft.value = false;
    }
  };

  const submit = () => {
    if (!order.items.length) return;

    if (savingDraft.value) {
      saveDraft();
    } else {
      checkoutOrder();
    }
  };

  return {
    submit,
    saveDraft,
    goToPayment,
  };
};
